import HostHelpSection from "@/components/HostHelpSection";
import { BodyContainer } from "@/components/Layout";
import { InlineSpinner } from "@/components/Spinner";
import { useQueryOptionsFactory } from "@/queries";
import { useActiveClient } from "@/routes/$clientId.tsx";
import { Alert } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import ConfigurationCard from "./-components/ConfigurationCard.tsx";
import EventList from "./-components/EventList.tsx";

const Root = (): ReactNode => {
    const { id: clientId } = useActiveClient();
    const params = Route.useParams();
    const qof = useQueryOptionsFactory();
    const { data: configuration } = useSuspenseQuery(
        qof.dynamicDocket.getConfiguration(clientId, params.hostId),
    );
    const { data: events } = useSuspenseQuery(
        qof.dynamicDocket.listUpcomingEvents(clientId, params.hostId),
    );

    return (
        <div>
            <ConfigurationCard configuration={configuration} />

            {events.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    There are currently no upcoming events configured.
                </Alert>
            ) : (
                <EventList events={events} sx={{ mt: 2 }} />
            )}

            <HostHelpSection
                supportResources={[
                    { label: "Using OptiFlex Dynamic Docket", type: "dynamic_docket" },
                ]}
            />
        </div>
    );
};

export const Route = createFileRoute("/$clientId/hosts/$hostId/dynamic-docket/")({
    component: Root,
    pendingComponent: () => (
        <BodyContainer>
            <InlineSpinner />
        </BodyContainer>
    ),
    loader: async ({ context, params }) => {
        await Promise.all([
            context.queryClient.ensureQueryData(
                context.qof.dynamicDocket.getConfiguration(params.clientId, params.hostId),
            ),
            context.queryClient.ensureQueryData(
                context.qof.dynamicDocket.listUpcomingEvents(params.clientId, params.hostId),
            ),
        ]);
    },
});
